import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

import { Button, Tooltip } from '@/ui';

import './styles.css';
import type { Props } from './types';

/**
 * Says whose layout is on screen while a role's default is being drafted.
 *
 * Without it a reader dragging cards around would have no sign that they are
 * arranging someone else's page rather than their own. It says that in as few
 * words as will fit on one line, because the pages that sort cards are dense
 * already: what each control does is in its tooltip, not next to it.
 */
const CardOrderDraftBar = ({
  discardDraft,
  editingRoleName,
  exitEditing,
  isDirty,
  isSaving,
  saveDraft
}: Props) => {
  if (!editingRoleName) return null;

  return (
    <div className="mb-2 flex flex-wrap items-center gap-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
      <Tooltip
        content={`Drag the cards to change the order ${editingRoleName} sees by default. Your own order is untouched.`}>
        <span className="flex items-center gap-1.5 font-medium">
          <FontAwesomeIcon icon={faLayerGroup} />
          {editingRoleName} default
          {isDirty && <span className="font-normal italic">unsaved</span>}
        </span>
      </Tooltip>

      <Tooltip
        content={`Replaces the ${editingRoleName} default for everyone in the role who has not set their own order`}>
        <Button
          color="primary"
          disabled={!isDirty}
          isLoading={isSaving}
          onClick={() => void saveDraft()}
          size="xs">
          Save
        </Button>
      </Tooltip>

      {isDirty ? (
        <Tooltip content={`Drops these changes and shows the saved ${editingRoleName} default`}>
          <Button color="neutral" onClick={discardDraft} size="xs" variant="outline">
            Discard
          </Button>
        </Tooltip>
      ) : (
        <Tooltip content="Stops editing and shows your own card order again">
          <Button color="neutral" onClick={exitEditing} size="xs" variant="ghost">
            Done
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

export default React.memo(CardOrderDraftBar);
